import { Badge } from '@/components/ui/primitives';
import type { PublishState } from '@/types/social';

/* Honest, connector-ready publishing-state chip (the PUBLISH axis only).
 *
 * Surfaces delivery posture WITHOUT ever fabricating a published state:
 *  - 'published' renders ONLY when a genuine provider receipt (providerRef)
 *    exists. A 'published' value without a providerRef is downgraded to the
 *    honest "Receipt missing" — it is never shown as published.
 *  - Lane D shipped the real connector: publishing now happens via a gated
 *    Publish action on an approved draft, backed by provider receipts.
 *    'connection_required' is a legacy persisted state from before that
 *    connector existed — it no longer means "no connection is available"; it
 *    means "on the calendar, nothing publishes automatically yet", so it
 *    renders as a neutral "Scheduled — not auto-published" rather than the
 *    old, now-misleading "Connection required" wording.
 *  - The chip stays connector-honest: 'queued'/'failed'/'published' render
 *    truthfully (with a retry affordance on failure); nothing is ever shown
 *    as published without a receipt on file. */

type Tone = 'neutral' | 'ai' | 'success' | 'warning' | 'danger';

export interface PublishChip {
  label: string;
  tone: Tone;
  retryable: boolean;
  help: string;
}

/** Map the publish axis to exactly one honest chip. Never claims published
 *  without a provider receipt. */
export function publishChip(publishState: PublishState, providerRef: string | null): PublishChip {
  switch (publishState) {
    case 'published':
      // The sole honest published state — requires a real external receipt.
      return providerRef
        ? { label: 'Published', tone: 'success', retryable: false, help: 'Delivered to the provider (external id on file).' }
        : { label: 'Receipt missing', tone: 'warning', retryable: false, help: 'Marked published but no provider receipt is on file — cannot be shown as published.' };
    case 'failed':
      return { label: 'Publish failed', tone: 'danger', retryable: true, help: 'The provider rejected this post. Retry once a connection is available.' };
    case 'queued':
      return { label: 'Queued', tone: 'ai', retryable: false, help: 'Waiting for the connector to deliver (owner-gated).' };
    case 'connection_required':
      return { label: 'Scheduled — not auto-published', tone: 'neutral', retryable: false, help: 'On the calendar. Nothing publishes automatically — publishing happens via the Publish action on an approved draft.' };
    case 'none':
    default:
      return { label: 'Not scheduled', tone: 'neutral', retryable: false, help: 'Not placed on the calendar for publishing.' };
  }
}

export function PublishStateChip({ publishState, providerRef, onRetry }: {
  publishState: PublishState;
  providerRef?: string | null;
  onRetry?: () => void;
}) {
  const chip = publishChip(publishState, providerRef ?? null);
  return (
    <span className="inline-flex items-center gap-1.5" title={chip.help}>
      <Badge tone={chip.tone}>{chip.label}</Badge>
      {chip.retryable && onRetry && (
        <button type="button" onClick={onRetry} className="text-xs text-ai underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai">
          Retry
        </button>
      )}
    </span>
  );
}
